import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Copy, Check, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { submitRegistration, joinWaitlist } from "@/lib/api";

const personas = ["Business Owner","Entrepreneur","Freelancer","Professional","Student","Aspiring Builder","Other"];

export default function RegistrationForm({ settings, onDone }) {
  const nav = useNavigate();
  const soldOut = settings?.seats?.remaining <= 0;
  const priceActive = settings?.early_bird_active ? settings.early_bird_price : settings.regular_price;
  const [copied, setCopied] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [form, setForm] = useState({ full_name:"", email:"", mobile_number:"", participant_type:"", project_idea:"", gcash_reference:"" });
  const fileRef = useRef();

  const set = (k,v) => setForm(f => ({...f, [k]:v}));

  async function submit(e) {
    e.preventDefault();
    if (soldOut) return submitWaitlist();
    if (!confirm) return toast.error("Please confirm your information");
    if (!file) return toast.error("Please attach your payment screenshot");
    if (!form.full_name || !form.email || !form.mobile_number || !form.participant_type || !form.gcash_reference)
      return toast.error("Please complete all required fields");

    const fd = new FormData();
    Object.entries(form).forEach(([k,v]) => fd.append(k, v));
    fd.append("confirm", "true");
    fd.append("payment_proof", file);

    setBusy(true);
    try {
      const res = await submitRegistration(fd);
      toast.success("Reservation received!");
      nav("/registration-success", { state: { ...res, settings } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  }

  async function submitWaitlist() {
    if (!form.full_name || !form.email || !form.mobile_number || !form.participant_type)
      return toast.error("Please complete all required fields");
    setBusy(true);
    try {
      await joinWaitlist({ full_name: form.full_name, email: form.email, mobile_number: form.mobile_number, participant_type: form.participant_type });
      toast.success("You're on the waitlist. We'll be in touch.");
      setForm({ full_name:"", email:"", mobile_number:"", participant_type:"", project_idea:"", gcash_reference:"" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  }

  return (
    <form id="register" onSubmit={submit} data-testid="registration-form" className="glass p-6 sm:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">{soldOut ? "Join Waitlist" : "Reserve Your Seat"}</div>
          <h3 className="text-2xl font-bold text-white mt-1">
            {soldOut ? "We're sold out — get on the list" : `Complete your reservation — ₱${priceActive}`}
          </h3>
        </div>
        <div className="chip"><span className="pulse-dot"/> {settings?.seats?.status}</div>
      </div>

      {!soldOut && (
        <div className="bg-cyan-400/[0.04] border border-cyan-400/20 rounded-xl p-4 sm:p-5 mb-6">
          <div className="text-cyan-300 font-mono text-[11px] tracking-widest uppercase mb-2">Step 1 — Pay via GCash</div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-white font-semibold text-lg tabular-nums">{settings.gcash_number}</div>
            <button type="button" data-testid="gcash-number-copy-btn"
              onClick={() => { navigator.clipboard.writeText(settings.gcash_number); setCopied(true); setTimeout(()=>setCopied(false),1500); }}
              className="inline-flex items-center gap-2 text-xs text-cyan-200 border border-cyan-400/30 px-3 py-1.5 rounded-full hover:bg-cyan-400/10">
              {copied ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-slate-400 text-xs mt-2">Save your GCash reference number and screenshot — you'll need them below.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Full Name *">
          <Input data-testid="input-full-name" value={form.full_name} onChange={e=>set("full_name", e.target.value)} placeholder="Juan Dela Cruz" required/>
        </Field>
        <Field label="Email Address *">
          <Input data-testid="input-email" type="email" value={form.email} onChange={e=>set("email", e.target.value)} placeholder="you@email.com" required/>
        </Field>
        <Field label="Mobile Number *">
          <Input data-testid="input-phone" value={form.mobile_number} onChange={e=>set("mobile_number", e.target.value)} placeholder="09XX XXX XXXX" required/>
        </Field>
        <Field label="Participant Type *">
          <Select value={form.participant_type} onValueChange={v=>set("participant_type", v)}>
            <SelectTrigger data-testid="select-persona" className="bg-white/[0.03] border-white/10"><SelectValue placeholder="Choose one"/></SelectTrigger>
            <SelectContent>
              {personas.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <div className="md:col-span-2">
          <Field label="What would you like to build? (optional)">
            <Textarea rows={3} value={form.project_idea} onChange={e=>set("project_idea", e.target.value)} placeholder="A CRM for my clinic, a booking site for my studio..."/>
          </Field>
        </div>

        {!soldOut && (
          <>
            <Field label="GCash Reference Number *">
              <Input data-testid="input-gcash-ref" value={form.gcash_reference} onChange={e=>set("gcash_reference", e.target.value)} placeholder="e.g. 1234567890123"/>
            </Field>
            <Field label="Payment Screenshot *">
              <div className="flex items-center gap-3">
                <input ref={fileRef} data-testid="input-payment-proof" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={e=>setFile(e.target.files?.[0])} className="hidden"/>
                <button type="button" onClick={()=>fileRef.current?.click()}
                  className="flex-1 flex items-center justify-between bg-white/[0.03] border border-dashed border-cyan-400/30 rounded-lg px-4 py-2.5 text-sm hover:bg-cyan-400/5">
                  <span className={file ? "text-cyan-200" : "text-slate-400"}>{file ? file.name : "Upload JPG / PNG / WEBP / PDF"}</span>
                  <Upload className="w-4 h-4 text-cyan-300"/>
                </button>
              </div>
            </Field>
          </>
        )}
      </div>

      {!soldOut && (
        <label className="flex items-start gap-3 mt-6 text-sm text-slate-300">
          <Checkbox data-testid="input-confirm" checked={confirm} onCheckedChange={setConfirm} className="mt-0.5 border-cyan-400/40"/>
          <span>I confirm that the information submitted is correct and understand that my seat is confirmed only after payment verification.</span>
        </label>
      )}

      <button
        data-testid={soldOut ? "waitlist-submit-btn" : "registration-submit-btn"}
        type="submit" disabled={busy}
        className="cta-primary mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin"/>}
        {soldOut ? "JOIN WAITLIST" : "SUBMIT RESERVATION"}
      </button>

      <p className="text-slate-500 text-xs mt-4">
        Your seat is officially confirmed only after payment verification.
      </p>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
